import { useEffect, useState, useRef } from "react";
import { Upload, Trash2, Loader2, Image as ImageIcon, Video, Eye, EyeOff, X, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SERVICE_OPTIONS = [
    { value: "none", label: "Sem categoria" },
    { value: "sofa", label: "Sofás" },
    { value: "colchao", label: "Colchões" },
    { value: "cadeira", label: "Cadeiras" },
    { value: "carpete", label: "Carpetes" },
    { value: "poltrona", label: "Poltronas" },
    { value: "alcatifa", label: "Alcatifas" },
    { value: "outro", label: "Outro" },
];

export default function AdminGallery() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [pairing, setPairing] = useState(null);
    const [pairRole, setPairRole] = useState("before");
    const [pairPartner, setPairPartner] = useState("");
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState("");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [serviceKey, setServiceKey] = useState("none");
    const fileRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/gallery");
            setItems(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const onPickFile = (f) => {
        if (!f) return;
        setFile(f);
        setPreview(URL.createObjectURL(f));
    };

    const resetForm = () => {
        setFile(null); setPreview(""); setTitle(""); setDescription(""); setServiceKey("none");
        if (fileRef.current) fileRef.current.value = "";
    };

    const upload = async () => {
        if (!file) return toast.error("Selecione um ficheiro");
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("title", title);
            form.append("description", description);
            if (serviceKey && serviceKey !== "none") form.append("service_key", serviceKey);
            await api.post("/admin/gallery", form, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Adicionado à galeria!");
            setOpen(false);
            resetForm();
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setUploading(false); }
    };

    const toggleActive = async (item) => {
        await api.patch(`/admin/gallery/${item.id}`, { active: !item.active });
        load();
    };

    const remove = async (item) => {
        if (!window.confirm("Eliminar este item da galeria?")) return;
        await api.delete(`/admin/gallery/${item.id}`);
        toast.success("Removido");
        load();
    };

    const savePair = async () => {
        if (!pairing) return;
        try {
            const pairId = pairing.pair_id || `pair-${Date.now()}`;
            await api.patch(`/admin/gallery/${pairing.id}`, { pair_id: pairId, pair_role: pairRole });
            if (pairPartner) {
                const otherRole = pairRole === "before" ? "after" : "before";
                await api.patch(`/admin/gallery/${pairPartner}`, { pair_id: pairId, pair_role: otherRole });
            }
            toast.success("Emparelhado!");
            setPairing(null); setPairPartner(""); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const unpair = async (item) => {
        await api.patch(`/admin/gallery/${item.id}`, { pair_id: null, pair_role: null });
        toast.success("Desemparelhado");
        load();
    };

    return (
        <div className="space-y-6" data-testid="admin-gallery">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight">Galeria de trabalhos</h1>
                    <p className="text-white/60">Fotografias e vídeos de serviços realizados — visíveis na página inicial.</p>
                </div>
                <Button onClick={() => setOpen(true)} className="ab-btn-primary rounded-none uppercase tracking-widest" data-testid="add-gallery">
                    <Upload className="w-4 h-4 mr-2" /> Adicionar Media
                </Button>
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div>
            ) : items.length === 0 ? (
                <div className="ab-card p-16 text-center">
                    <ImageIcon className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/60">Ainda não há trabalhos publicados. Adicione fotos ou vídeos dos serviços realizados.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {items.map((g) => (
                        <div key={g.id} className="ab-card overflow-hidden group" data-testid={`gallery-item-${g.id}`}>
                            <div className="relative aspect-square bg-black">
                                {g.media_type === "video" ? (
                                    <video src={fileUrl(g.storage_path)} className="w-full h-full object-cover" controls muted playsInline />
                                ) : (
                                    <img src={fileUrl(g.storage_path)} alt={g.title} className="w-full h-full object-cover" />
                                )}
                                <div className="absolute top-2 left-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-widest bg-black/70 text-white px-2 py-1 rounded">
                                        {g.media_type === "video" ? <Video className="w-3 h-3 inline" /> : <ImageIcon className="w-3 h-3 inline" />} {g.media_type}
                                    </span>
                                </div>
                                {!g.active && (
                                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                        <span className="text-xs uppercase tracking-widest text-white font-bold">Oculto</span>
                                    </div>
                                )}
                            </div>
                            <div className="p-4">
                                <div className="font-heading font-bold text-sm text-white truncate">{g.title || "Sem título"}</div>
                                {g.service_key && <div className="text-[10px] uppercase tracking-widest text-[#C9A961] mt-1">{g.service_key}</div>}
                                <div className="flex gap-1 mt-3">
                                    <Button size="sm" variant="ghost" onClick={() => toggleActive(g)} className="flex-1 text-white/70 hover:bg-[#1F1F1F]" data-testid={`toggle-gallery-${g.id}`}>
                                        {g.active ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setPairing(g)} className="flex-1 text-[#C9A961] hover:bg-[#C9A961]/10" title="Emparelhar Antes/Depois" data-testid={`pair-gallery-${g.id}`}>
                                        {g.pair_role ? <span className="text-[10px] font-bold uppercase">{g.pair_role === "before" ? "Antes" : "Depois"}</span> : <ArrowLeftRight className="w-3.5 h-3.5" />}
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => remove(g)} className="flex-1 text-red-400 hover:bg-red-500/10" data-testid={`delete-gallery-${g.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!pairing} onOpenChange={(v) => !v && setPairing(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Emparelhar Antes / Depois</DialogTitle></DialogHeader>
                    {pairing && (
                        <div className="space-y-4">
                            <p className="text-sm text-white/60">Item selecionado: <b className="text-white">{pairing.title || "Sem título"}</b></p>
                            <div>
                                <Label>Papel deste item</Label>
                                <div className="flex gap-2 mt-2">
                                    <Button size="sm" onClick={() => setPairRole("before")} className={pairRole === "before" ? "ab-btn-primary rounded-none uppercase tracking-widest" : "bg-[#1F1F1F] text-white/70 hover:bg-[#2A2A2A] rounded-none uppercase tracking-widest"}>Antes</Button>
                                    <Button size="sm" onClick={() => setPairRole("after")} className={pairRole === "after" ? "ab-btn-primary rounded-none uppercase tracking-widest" : "bg-[#1F1F1F] text-white/70 hover:bg-[#2A2A2A] rounded-none uppercase tracking-widest"}>Depois</Button>
                                </div>
                            </div>
                            <div>
                                <Label>Emparelhar com (opcional)</Label>
                                <Select value={pairPartner} onValueChange={setPairPartner}>
                                    <SelectTrigger><SelectValue placeholder="Selecionar item par" /></SelectTrigger>
                                    <SelectContent>
                                        {items.filter((x) => x.id !== pairing.id && x.media_type === "image").map((x) => (
                                            <SelectItem key={x.id} value={x.id}>{x.title || "Sem título"}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-white/50 mt-1">Se deixar vazio, apenas este item recebe o papel.</p>
                            </div>
                            {pairing.pair_id && (
                                <Button size="sm" variant="ghost" onClick={() => { unpair(pairing); setPairing(null); }} className="text-red-400 hover:bg-red-500/10">Remover emparelhamento</Button>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setPairing(null)}>Cancelar</Button>
                        <Button onClick={savePair} className="ab-btn-primary rounded-none uppercase tracking-widest">Guardar par</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>


            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Adicionar à galeria</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Ficheiro (imagem ou vídeo)</Label>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*,video/*"
                                onChange={(e) => onPickFile(e.target.files?.[0])}
                                className="block w-full text-sm text-white/80 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-[#C9A961] file:text-black file:font-semibold file:uppercase file:tracking-widest file:text-xs hover:file:bg-[#B39149] cursor-pointer"
                                data-testid="gallery-file-input"
                            />
                            {preview && (
                                <div className="mt-3 relative">
                                    {file?.type?.startsWith("video") ? (
                                        <video src={preview} controls className="w-full max-h-56 object-cover rounded-md" />
                                    ) : (
                                        <img src={preview} alt="preview" className="w-full max-h-56 object-cover rounded-md" />
                                    )}
                                    <button onClick={() => { setFile(null); setPreview(""); if (fileRef.current) fileRef.current.value = ""; }} className="absolute top-2 right-2 w-7 h-7 bg-black/80 rounded-full flex items-center justify-center text-white">
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            )}
                        </div>
                        <div>
                            <Label>Título</Label>
                            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Sofá em Lousada" data-testid="gallery-title" />
                        </div>
                        <div>
                            <Label>Descrição (opcional)</Label>
                            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="gallery-description" />
                        </div>
                        <div>
                            <Label>Categoria de serviço</Label>
                            <Select value={serviceKey} onValueChange={setServiceKey}>
                                <SelectTrigger data-testid="gallery-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {SERVICE_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={upload} disabled={uploading || !file} className="ab-btn-primary rounded-none uppercase tracking-widest" data-testid="upload-gallery-submit">
                            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                            Publicar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
